import { useState, useEffect, useContext } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";

import useSidebar from "../../hooks/useSidebar";
import SidebarFooter from "./SidebarFooter";
import SidebarNav from "./SidebarNav";
import logo from "../../assets/img/logo.png";
import RevenueTeamLabel from "../RevenueTeamLabel";

import { UserSettingsContext } from "../../contexts/UserSettingsContext";

import authenticatedFetch from "../../utils/fetch"

const Sidebar = ({ items, showFooter = false }) => {
  const { isOpen, setBlockedBadge, blockedBadge } = useSidebar();
  const { isH3Admin, authorities, navbarRefresh } = useContext(UserSettingsContext);

  // TODO - just move the item array here, but for now, repopulate the key from this ajax call

  const [data, setData] = useState([]);

  useEffect(() => {
    if (isH3Admin || authorities.includes("PRACTICE_BASIC_VIEW") || authorities.includes("PRACTICE_BASIC_EDIT"))
    {
      authenticatedFetch({
        path: "practice/revenueteam",
        successHandler: function(result) {
          setData(result);
        }
      });
    }
  }, [isH3Admin, authorities, navbarRefresh]);

  let filteredItems = [];

  // Has any enrollment access
  if (isH3Admin || authorities.includes("TASK_VIEW") || authorities.includes("TASK_EDIT")
  || authorities.includes("PRACTICE_BASIC_VIEW") || authorities.includes("PRACTICE_BASIC_EDIT")
  || authorities.includes("PROVIDER_BASIC_VIEW") || authorities.includes("PROVIDER_BASIC_EDIT"))
  {
    let filteredEnrollmentPages = [];

    // They get (some portion at least) of the enrollment dashboard
    filteredEnrollmentPages.push(items[0].pages[0]);

    // Tasks
    if (isH3Admin || authorities.includes("TASK_VIEW") || authorities.includes("TASK_EDIT"))
    {
        // Do it on load (and then on tasks page any time we update a task... doesn't work if someone else who is logged in does it...
      authenticatedFetch({
        path: "task/blocked",
        successHandler: function(result) {
          setBlockedBadge(result)
        }
      });

      if (blockedBadge == null || blockedBadge <= 0)
      {
        items[0].pages[1].badge = null;
      }
      else
      {
        items[0].pages[1].badge = blockedBadge;
      }

      filteredEnrollmentPages.push(items[0].pages[1]);
    }

    if (isH3Admin || authorities.includes("PRACTICE_BASIC_VIEW") || authorities.includes("PRACTICE_BASIC_EDIT"))
    {
      const compareCaseInsensitive = (a, b) => (a || "").localeCompare(b || "", undefined, { sensitivity: "base" });

      const sortedData = data && data.length > 0
        ? [...data].sort((a, b) => compareCaseInsensitive(a.name, b.name))
        : data;

      let practiceList = sortedData && sortedData.length > 0
      && sortedData.map((practice, i) => {
        return (
          {
            href: "/practice/" + practice.id,
            title: "• " + practice.name
          }
        )
      }, this);

      const hasGrouping = sortedData && sortedData.some((practice) => practice.grouping && practice.grouping.trim());

      if (practiceList && hasGrouping)
      {
        const groups = {};
        const groupLabels = {};
        const ungrouped = [];

        sortedData.forEach((practice) => {
          const practiceItem = {
            href: "/practice/" + practice.id,
            title: "• " + practice.name
          };

          const rawGrouping = practice.grouping && practice.grouping.trim();
          if (rawGrouping)
          {
            const normalizedGrouping = rawGrouping.toUpperCase();
            if (!groups[normalizedGrouping])
            {
              groups[normalizedGrouping] = [];
              // First casing seen for this grouping wins as the display label
              groupLabels[normalizedGrouping] = rawGrouping;
            }
            groups[normalizedGrouping].push(practiceItem);
          }
          else
          {
            ungrouped.push(practiceItem);
          }
        });

        practiceList = Object.keys(groups).sort(compareCaseInsensitive).map((normalizedGrouping) => {
          return (
            {
              title: groupLabels[normalizedGrouping],
              children: groups[normalizedGrouping]
            }
          )
        }).concat(ungrouped);
      }

      items[0].pages[2].children = practiceList;

      filteredEnrollmentPages.push(items[0].pages[2]);
    }

    var copy = {};
    Object.assign(copy, items[0]);
    copy.pages = filteredEnrollmentPages;

    filteredItems.push(copy);
  }

  if (authorities.includes("MESSAGING_VIEW") || authorities.includes("MESSAGING_EDIT") || isH3Admin)
  {
    // Both share the same permissions, so just add them
    filteredItems.push(items[2]);
  }

  if (isH3Admin || authorities.includes("PROVIDER_SENSITIVE_DETAILS_VIEW") || authorities.includes("PROVIDER_SENSITIVE_DETAILS_EDIT")
    || authorities.includes("PROVIDER_DOCUMENT_VIEW") || authorities.includes("PROVIDER_DOCUMENT_EDIT")
    || authorities.includes("PROVIDER_FACILITY_VIEW") || authorities.includes("PROVIDER_FACILITY_EDIT")
    || authorities.includes("PROVIDER_CME_VIEW") || authorities.includes("PROVIDER_CME_EDIT")
    || authorities.includes("PROVIDER_BOARD_CERTIFICATION_VIEW") || authorities.includes("PROVIDER_BOARD_CERTIFICATION_EDIT")
    || authorities.includes("PROVIDER_IMMUNIZATION_VIEW") || authorities.includes("PROVIDER_IMMUNIZATION_EDIT")
    || authorities.includes("PRACTICE_BASIC_VIEW") || authorities.includes("PRACTICE_BASIC_EDIT")
    || authorities.includes("KPI_VIEW") || authorities.includes("KPI_EDIT")
    || authorities.includes("TASK_VIEW") || authorities.includes("TASK_EDIT"))
  {
    let filteredReportPages = [];

    if (isH3Admin || authorities.includes("KPI_VIEW") || authorities.includes("KPI_EDIT"))
    {
      // KPI reports
      filteredReportPages.push(items[3].pages[0]);
    }

    if (isH3Admin || authorities.includes("PROVIDER_SENSITIVE_DETAILS_VIEW") || authorities.includes("PROVIDER_SENSITIVE_DETAILS_EDIT")
      || authorities.includes("PROVIDER_DOCUMENT_VIEW") || authorities.includes("PROVIDER_DOCUMENT_EDIT")
      || authorities.includes("PROVIDER_FACILITY_VIEW") || authorities.includes("PROVIDER_FACILITY_EDIT")
      || authorities.includes("PROVIDER_CME_VIEW") || authorities.includes("PROVIDER_CME_EDIT")
      || authorities.includes("PROVIDER_BOARD_CERTIFICATION_VIEW") || authorities.includes("PROVIDER_BOARD_CERTIFICATION_EDIT")
      || authorities.includes("PROVIDER_IMMUNIZATION_VIEW") || authorities.includes("PROVIDER_IMMUNIZATION_EDIT"))
    {
      // Provider reports and rosters
      filteredReportPages.push(items[3].pages[1]);
      filteredReportPages.push(items[3].pages[2]);
    }

    if (isH3Admin || authorities.includes("PRACTICE_BASIC_VIEW") || authorities.includes("PRACTICE_BASIC_EDIT"))
    {
      // Practice reports
      filteredReportPages.push(items[3].pages[3]);
    }

    if (isH3Admin || authorities.includes("TASK_VIEW") || authorities.includes("TASK_EDIT"))
    {
      // Task reports
      filteredReportPages.push(items[3].pages[4]);
    }

    // // TODO new permissions
    // if (isH3Admin)
    // {
    //   // Audit reports
    //   filteredReportPages.push(items[4].pages[5]);
    // }

    var copy = {};
    Object.assign(copy, items[3]);
    copy.pages = filteredReportPages;

    filteredItems.push(copy);
  }

  // Has any admin
  if (isH3Admin || authorities.includes("ADMIN_PROVIDER_MANAGEMENT_VIEW") || authorities.includes("ADMIN_PROVIDER_MANAGEMENT_EDIT")
  || authorities.includes("ADMIN_PRACTICE_MANAGEMENT_VIEW") || authorities.includes("ADMIN_PRACTICE_MANAGEMENT_EDIT")
  || authorities.includes("ADMIN_USER_MANAGEMENT_VIEW") || authorities.includes("ADMIN_USER_MANAGEMENT_EDIT"))
  {
    let filteredAdminPages = [];

    // Provider management
    if (isH3Admin || authorities.includes("ADMIN_PROVIDER_MANAGEMENT_VIEW") || authorities.includes("ADMIN_PROVIDER_MANAGEMENT_EDIT"))
    {
      filteredAdminPages.push(items[4].pages[0]);
    }

    // Practice management
    if (isH3Admin || authorities.includes("ADMIN_PRACTICE_MANAGEMENT_VIEW") || authorities.includes("ADMIN_PRACTICE_MANAGEMENT_EDIT"))
    {
      filteredAdminPages.push(items[4].pages[1]);
    }

    // User management
    if (isH3Admin || authorities.includes("ADMIN_USER_MANAGEMENT_VIEW") || authorities.includes("ADMIN_USER_MANAGEMENT_EDIT"))
    {
      filteredAdminPages.push(items[4].pages[2]);
    }

    // Carrier management
    if (isH3Admin)
    {
      filteredAdminPages.push(items[4].pages[3]);
    }

    // Facility management
    if (isH3Admin)
    {
      filteredAdminPages.push(items[4].pages[4]);
    }

    // Revenue team management
    if (isH3Admin && (authorities.includes("H3_ADMIN_DATA_DELETION_VIEW") || authorities.includes("H3_ADMIN_DATA_DELETION_EDIT")))
    {
      filteredAdminPages.push(items[4].pages[5]);
    }

    var copy = {};
    Object.assign(copy, items[4]);
    copy.pages = filteredAdminPages;

    filteredItems.push(copy);
  }


  return (
    <nav className={`sidebar ${!isOpen ? "collapsed" : ""}`}>
      <div className="sidebar-content">
        <PerfectScrollbar>
          <a className="sidebar-brand" href="/">
            <img alt="H3" src={logo} width={25} height={25} />
            &nbsp;
            <span className="align-middle me-3">H3 Elevate</span>
            <br /><br />
            <h5 className="align-middle me-3" style={{color: "#2B7A78", fontSize: "0.8em"}}>
              <RevenueTeamLabel />
            </h5>
          </a>

          <SidebarNav items={filteredItems} />
          {!!showFooter && <SidebarFooter />}
        </PerfectScrollbar>
      </div>
    </nav>
  );
};

export default Sidebar;
